import React, { useState } from 'react';
import { Space, Switch, Typography } from 'antd';
import styled from 'styled-components';
import { format, parseISO } from 'date-fns';
import { IReportSchedule } from 'contexts/reporting/types';
import { screenMd } from 'constants/styles/breakpoints';

const { Text } = Typography;

const StyledSwitch = styled(Switch)`
  &.ant-switch-checked {
    background-color: ${({ theme }) => theme.default.colors.palette.Business_DataVizLilacLighter};
  }
`;

const StyledScheduleLabel = styled(Text)`
  font-size: 9px;
  font-style: normal;
  font-weight: 450;
  line-height: 14px;

  ${screenMd} {
    font-size: 12px;
  }
`;

const StyledStatusLabel = styled(Text)`
  display: block;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  margin-bottom: 4px;
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  white-space: nowrap;
`;

type PauseSwitchCellProps = {
  isPaused: boolean;
  record: IReportSchedule;
  loading: boolean;
  onChange: (value: boolean) => void;
};

const formatDate = (date: string) => {
  try {
    return format(parseISO(date), 'MMM d, yyyy');
  } catch (error) {
    return 'Invalid date';
  }
};

const PauseSwitchCell: React.FC<PauseSwitchCellProps> = ({
  isPaused,
  record,
  loading,
  onChange,
}) => {
  const [paused, setPaused] = useState(isPaused);

  const handleChange = (checked: boolean) => {
    const newPausedState = !checked;
    setPaused(newPausedState);
    onChange(newPausedState);
  };

  return (
    <Space align="center">
      <StyledSwitch disabled={loading} checked={!paused} onChange={handleChange} />
      <LabelsContainer>
        {paused ? (
          <>
            <StyledStatusLabel>Paused - Not sending</StyledStatusLabel>
            {record.last_executed_date && (
              <StyledScheduleLabel type="secondary">
                Last send: {formatDate(record.last_executed_date)}
              </StyledScheduleLabel>
            )}
          </>
        ) : (
          <>
            <StyledStatusLabel>Scheduled and active</StyledStatusLabel>
            <StyledScheduleLabel type="secondary">
              Next scheduled: {formatDate(record.next_execution_date)}
            </StyledScheduleLabel>
          </>
        )}
      </LabelsContainer>
    </Space>
  );
};

export default PauseSwitchCell;
